import { Terrain } from './terrain';
import type { Projectile } from './physics';
import type { EdgeMode } from './physics';
import { stepProjectile } from './physics';
import { collapseTick, settleTank } from './collapse';
import { calcDamage, applyBurialDamage } from './damage';
import { mulberry32, randRange, type Rng } from './rng';
import { placeTanks } from './placement';
import { advanceTurn, checkRoundOver } from './turn';
import { Pool } from './pool';
import { detonateWeapon, checkMirvApex, stepRoller, stepDigger, handleLeapfrogBounce } from './weapon-behaviors';
import { WEAPONS_BY_ID } from './weapons';
import { type ActiveEffect, stepEffects } from './active-effects';

// Pure-sim world size constants. Render layer imports these to size its canvas.
export const WORLD_W = 1600;
export const WORLD_H = 900;

export const PLAYER_COLORS = [
  { name: 'Red',    hex: '#e6194b', rgb: [230, 25, 75] as const },
  { name: 'Blue',   hex: '#4363d8', rgb: [67, 99, 216] as const },
  { name: 'Yellow', hex: '#ffe119', rgb: [255, 225, 25] as const },
  { name: 'Teal',   hex: '#42d4f4', rgb: [66, 212, 244] as const },
] as const;

export interface Tank {
  id: number;
  x: number;
  y: number;
  hp: number;
  alive: boolean;
  // Phase 3 additions (TNK-01)
  angle: number;        // degrees 0-180 (per-player aim state)
  power: number;        // 0-1000 (per-player aim state)
  color: string;        // hex color from PLAYER_COLORS
  name: string;         // "Player 1", "Player 2", etc.
  shields: number;      // shield charges (0 for Phase 3)
  parachutes: number;   // parachute charges (default 3)
  fuel: number;         // movement fuel (0 for Phase 3)
  flashTimer: number;   // seconds remaining for hit-flash effect
  selectedWeapon: string;           // current weapon id
  inventory: Record<string, number>; // weapon id -> quantity
  activeShield: { defId: string; energy: number } | null;
  // Economy fields
  cash: number;         // current cash balance
  deathOrder: number;   // 0 = alive or never died, 1 = first to die, 2 = second, etc.
}

export type TurnPhase = 'aiming' | 'flying' | 'resolving' | 'round-over';

export interface KillEntry {
  killer: number;  // tank id
  victim: number;  // tank id
}

export interface TurnState {
  phase: TurnPhase;
  currentPlayer: number;
  roundNumber: number;
  killLog: KillEntry[];
  deathQueue: number[];  // tank indices pending death explosion
  windPerTurn: boolean;
}

export interface GameState {
  tick: number;
  seed: number;
  rng: Rng;
  terrain: Terrain;
  tanks: Tank[];
  projectiles: Pool<Projectile>;
  aimPreview: { visible: boolean; x: number; y: number };
  pendingFire: { angle: number; power: number } | null;
  world: { gravity: number; width: number; height: number; wind: number; edgeMode: EdgeMode };
  hasEverFired: boolean;
  collapsing: boolean;
  lastHit: { x: number; y: number; radius: number; weaponId?: string } | null;
  activeEffects: ActiveEffect[];
  turn: TurnState;
  resolvingTicks?: number;  // safety counter; auto-advances if stuck
}

export function newGameState(seed: number, playerCount = 4): GameState {
  const W = WORLD_W;
  const H = WORLD_H;
  const terrain = new Terrain(W, H);
  terrain.generate(seed);

  const rng = mulberry32(seed + 1); // offset so wind differs from terrain

  // Place tanks evenly across terrain
  const positions = placeTanks(playerCount, terrain, rng);
  const tanks: Tank[] = positions.map((pos, i) => ({
    id: i,
    x: pos.x,
    y: pos.y,
    hp: 100,
    alive: true,
    angle: 70,
    power: 500,
    color: PLAYER_COLORS[i % PLAYER_COLORS.length].hex,
    name: `Player ${i + 1}`,
    shields: 0,
    parachutes: 0,
    fuel: 0,
    flashTimer: 0,
    selectedWeapon: 'baby-missile',
    inventory: { 'baby-missile': Infinity },
    activeShield: null,
    cash: 0,          // set by caller via giveStarterInventory / applyStartingCash
    deathOrder: 0,
  }));

  return {
    tick: 0,
    seed,
    rng,
    terrain,
    tanks,
    projectiles: new Pool<Projectile>(32, () => ({
      active: false, x: 0, y: 0, vx: 0, vy: 0,
      weaponId: 'baby-missile', owner: 0, isSub: false,
      mode: 'flying' as const, prevVy: 0, bouncesLeft: 0, tunnelTraveled: 0,
    })),
    aimPreview: { visible: false, x: 0, y: 0 },
    pendingFire: null,
    world: {
      gravity: 200,
      width: W,
      height: H,
      wind: randRange(rng, -20, 20),
      edgeMode: 'none',
    },
    hasEverFired: false,
    collapsing: false,
    lastHit: null,
    activeEffects: [],
    turn: {
      phase: 'aiming',
      currentPlayer: 0,
      roundNumber: 1,
      killLog: [],
      deathQueue: [],
      windPerTurn: false,
    },
  };
}

const BLAST_RADIUS = 28;
const MAX_DAMAGE = 25;
const DEATH_BLAST_RADIUS = 20;
const DEATH_MAX_DAMAGE = 15;

export function detonateAt(
  state: GameState,
  x: number,
  y: number,
  blastRadius = BLAST_RADIUS,
  maxDamage = MAX_DAMAGE,
): void {
  // 1. Carve terrain
  state.terrain.carve(x, y, blastRadius);

  // 2. Apply damage to all tanks in range (ascending index for determinism)
  for (let i = 0; i < state.tanks.length; i++) {
    const tank = state.tanks[i];
    if (!tank.alive) continue;
    const dist = Math.hypot(tank.x - x, (tank.y - 1) - y);
    const dmg = calcDamage(dist, blastRadius, maxDamage);
    if (dmg > 0) {
      tank.hp -= dmg;
      tank.flashTimer = 0.15;
      if (tank.hp <= 0) {
        tank.alive = false;
        // Record death order for round-end place-point scoring.
        if (tank.deathOrder === 0) {
          const deadCount = state.tanks.filter(t => !t.alive).length;
          tank.deathOrder = deadCount; // 1 = first to die, 2 = second, ...
        }
        // Add to death queue if not already there (for chain kills)
        if (!state.turn.deathQueue.includes(i)) {
          state.turn.deathQueue.push(i);
        }
        // Record kill: credit goes to the player whose turn it is
        state.turn.killLog.push({
          killer: state.turn.currentPlayer,
          victim: tank.id,
        });
      }
    }
  }

  // 3. Start collapse sequence
  state.collapsing = true;

  // 4. Signal render layer for particles/shake
  state.lastHit = { x, y, radius: blastRadius };
}

export function stepSim(state: GameState, dt: number): void {
  state.tick++;

  const { turn } = state;

  // Decrement flash timers
  for (const tank of state.tanks) {
    if (tank.flashTimer > 0) {
      tank.flashTimer = Math.max(0, tank.flashTimer - dt);
    }
  }

  // --- AIMING phase: accept fire requests ---
  if (turn.phase === 'aiming') {
    if (state.pendingFire && state.projectiles.activeCount === 0 && !state.collapsing) {
      const { angle, power } = state.pendingFire;
      const launchSpeed = 75 + power * 525;
      const tank = state.tanks[turn.currentPlayer];
      const p = state.projectiles.acquire();
      if (p) {
        p.x = tank.x;
        p.y = tank.y - 4;
        p.vx = Math.cos(angle) * launchSpeed;
        p.vy = Math.sin(angle) * launchSpeed;
        p.weaponId = tank.selectedWeapon ?? 'baby-missile';
        p.owner = turn.currentPlayer;
        p.isSub = false;
        p.mode = 'flying';
        p.prevVy = 0;
        const wdef = WEAPONS_BY_ID[p.weaponId];
        p.bouncesLeft = wdef?.bounces ?? 0;
        p.tunnelTraveled = 0;
        // Auto-switch to baby-missile AFTER spawn if ammo is now depleted
        const remaining = tank.inventory[p.weaponId] ?? 0;
        if (remaining <= 0 && !wdef?.isFree) {
          tank.selectedWeapon = 'baby-missile';
        }
      }
      state.pendingFire = null;
      state.hasEverFired = true;
      turn.phase = 'flying';
    }
  }

  // --- FLYING phase: run projectile physics ---
  if (turn.phase === 'flying') {
    const toRelease: Projectile[] = [];
    state.projectiles.forEach(p => {
      // Rolling projectiles follow terrain surface
      if (p.mode === 'rolling') {
        if (stepRoller(state, p, dt)) {
          toRelease.push(p);
        }
        return;
      }
      // Tunneling projectiles carve through terrain
      if (p.mode === 'tunneling') {
        if (stepDigger(state, p, dt)) {
          toRelease.push(p);
        }
        return;
      }

      // Check MIRV apex split before physics step result
      if (checkMirvApex(state, p)) {
        toRelease.push(p);
        return;
      }

      const result = stepProjectile(
        p,
        state.terrain,
        state.world,
        state.tanks,
        dt,
      );
      if (result.type === 'hit' || result.type === 'hit-tank') {
        // Special mode transitions for roller and digger
        const def = WEAPONS_BY_ID[p.weaponId];
        if (def && def.behavior === 'digger') {
          p.mode = 'tunneling';
          p.x = result.x;
          p.y = result.y;
          // Diggers (baby-digger, digger, heavy-digger) go straight down
          // Sandhogs keep their flight direction
          const isSandhog = p.weaponId.includes('sandhog');
          if (!isSandhog) {
            p.vx = 0;
            p.vy = 100;
          } else {
            // Normalize and scale to tunneling speed
            const mag = Math.hypot(p.vx, p.vy) || 1;
            p.vx = (p.vx / mag) * 100;
            p.vy = (p.vy / mag) * 100;
          }
          p.tunnelTraveled = 0;
          p.active = true;
          return;
        }
        if (def && def.behavior === 'roller') {
          p.mode = 'rolling';
          p.y = result.y;
          p.x = result.x;
          p.vy = 0;
          p.vx = (p.vx >= 0 ? 1 : -1) * (def.rollSpeed ?? 80);
          p.tunnelTraveled = 0; // reuse for roll distance tracking
          p.active = true; // ensure still active
          return;
        }
        // Tracer: silently release, no detonation
        if (def && def.behavior === 'tracer') {
          toRelease.push(p);
          return;
        }
        // Leapfrog: detonate + re-launch if bounces remain
        if (def && def.behavior === 'leapfrog') {
          detonateWeapon(state, p.weaponId, result.x, result.y, p.vx, p.vy);
          handleLeapfrogBounce(state, p.weaponId, result.x, result.y, p.vx, p.bouncesLeft, p.owner);
          toRelease.push(p);
          return;
        }
        detonateWeapon(state, p.weaponId, result.x, result.y, p.vx, p.vy);
        toRelease.push(p);
      } else if (result.type === 'offscreen') {
        toRelease.push(p);
      }
    });
    for (const p of toRelease) {
      state.projectiles.release(p);
    }
    if (state.projectiles.activeCount === 0) {
      turn.phase = 'resolving';
    }
  }

  // --- RESOLVING phase: collapse, chain kills, settle ---
  if (turn.phase === 'resolving') {
    // Safety: force-advance if stuck in resolving for >10 sec real-time (~600 ticks)
    state.resolvingTicks = (state.resolvingTicks ?? 0) + 1;
    if (state.resolvingTicks > 600) {
      state.collapsing = false;
      state.activeEffects = [];
      state.turn.deathQueue = [];
      if (checkRoundOver(state)) turn.phase = 'round-over';
      else advanceTurn(state);
      state.resolvingTicks = 0;
      return;
    }
    if (state.collapsing) {
      // Run collapse up to 4x per tick so post-death dirt-fall doesn't stall
      // the game for ~1s on large craters (60px crater = 60 ticks at 1px/tick).
      // Still visible as animation since ticks are 60fps.
      let moved = false;
      for (let i = 0; i < 4; i++) {
        if (collapseTick(state.terrain)) moved = true;
        else break;
      }
      if (!moved) {
        state.collapsing = false;
        // Settle all alive tanks + check for burial/fall deaths
        for (const tank of state.tanks) {
          if (tank.alive) {
            const fallDistance = settleTank(tank, state.terrain);
            // Fall damage (parachute logic added in Plan 03-03)
            if (fallDistance > 8) {
              if (tank.parachutes > 0) {
                tank.parachutes--;
              } else {
                const fallDmg = Math.floor(fallDistance / 3);
                if (fallDmg > 0) {
                  tank.hp -= fallDmg;
                  tank.flashTimer = 0.15;
                  if (tank.hp <= 0) {
                    tank.alive = false;
                    if (tank.deathOrder === 0) {
                      tank.deathOrder = state.tanks.filter(t => !t.alive).length;
                    }
                    // Fall deaths do NOT trigger death explosions
                  }
                }
              }
            }
            applyBurialDamage(tank, state.terrain);
            // Check if burial killed the tank
            if (!tank.alive && !state.turn.deathQueue.includes(state.tanks.indexOf(tank))) {
              // Burial deaths do NOT chain (same as fall deaths)
            }
          }
        }
      }
    } else if (turn.deathQueue.length > 0) {
      // Process next death explosion
      const deadIdx = turn.deathQueue.shift()!;
      const dead = state.tanks[deadIdx];
      detonateAt(state, dead.x, dead.y, DEATH_BLAST_RADIUS, DEATH_MAX_DAMAGE);
      // detonateAt sets collapsing=true, so next tick will collapse first
    } else if (state.activeEffects.length > 0) {
      // Process active effects (napalm, liquid dirt)
      const stillActive = stepEffects(
        state.activeEffects,
        state.terrain,
        state.tanks,
        dt,
        state.world.width,
      );
      if (stillActive) {
        // Effects may have modified terrain, trigger collapse
        state.collapsing = true;
      }
    } else {
      // All resolution complete - check round over or advance turn
      if (checkRoundOver(state)) {
        turn.phase = 'round-over';
      } else {
        advanceTurn(state);
      }
      state.resolvingTicks = 0;
    }
  } else {
    state.resolvingTicks = 0;
  }

  // --- ROUND-OVER phase: no sim stepping, wait for UI ---
}
